import imageCaptionDefinitions from './imageCaptions.js';
import { setParallaxTranslation } from './parallaxMotion.js';

const imageCaptionModule = {
  init(definitions = imageCaptionDefinitions) {
    const container = document.querySelector('.pages-container');
    const strip = container?.querySelector('.pages-inner');
    if (!strip) return;
    const desktop = matchMedia('(min-width: 768px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const pages = Array.from(strip.querySelectorAll('.page'));
    const entries = [];
    for (const definition of definitions) {
      const page = pages.find(element => element.dataset.id === String(definition.page));
      if (!page || !definition.items?.length) continue;
      const group = document.createElement('section');
      group.className = 'image-caption-module';
      group.setAttribute('aria-label', definition.label || 'Production images');
      group.tabIndex = 0;
      for (const item of definition.items) {
        const card = document.createElement('figure');
        card.className = 'image-caption-module__item';
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.alt || '';
        image.loading = 'lazy';
        image.draggable = false;
        const caption = document.createElement('figcaption');
        const title = document.createElement('h3');
        title.textContent = item.title || '';
        const description = document.createElement('p');
        description.textContent = (item.description || '').replace(/<br\s*\/?\s*>/gi, '\n');
        caption.append(title, description);
        card.append(image, caption);
        group.append(card);
      }
      group.addEventListener('wheel', event => {
        if (!desktop.matches && Math.abs(event.deltaX) > Math.abs(event.deltaY)) event.stopPropagation();
      }, { passive: true });
      group.addEventListener('keydown', event => {
        if (desktop.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        const step = group.children[1]?.offsetLeft - group.children[0].offsetLeft || group.clientWidth;
        const current = Math.round(group.scrollLeft / step);
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? group.children.length - 1
          : current + (event.key === 'ArrowRight' ? 1 : -1);
        group.scrollTo({ left: Math.max(0, Math.min(group.children.length - 1, index)) * step,
          behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      });
      strip.append(group);
      entries.push({ definition, page, group });
    }
    if (entries.length) container.classList.add('has-image-captions');
    const update = () => {
      const stripBounds = strip.getBoundingClientRect();
      const viewport = container.getBoundingClientRect();
      const strength = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--image-parallax-amount')) || 0;
      for (const { definition, page, group } of entries) {
        const settings = desktop.matches ? definition : { ...definition, ...definition.mobile };
        const bounds = (page.querySelector('.filmstrip-stage') || page).getBoundingClientRect();
        const percentage = (value, fallback) => Number.parseFloat(value ?? fallback) / 100;
        const width = bounds.width * percentage(settings.width, 60);
        const rightAligned = settings.right != null && !( !desktop.matches && definition.mobile?.left != null);
        const bottomAligned = settings.bottom != null && !( !desktop.matches && definition.mobile?.top != null);
        group.style.width = `${width}px`;
        group.style.left = `${bounds.left - stripBounds.left + (rightAligned
          ? bounds.width * (1 - percentage(settings.right, 0)) - width
          : bounds.width * percentage(settings.left, 12))}px`;
        group.style.top = `${bounds.top - stripBounds.top + bounds.height * (bottomAligned
          ? 1 - percentage(settings.bottom, 0) : percentage(settings.top, 12))}px`;
        group.style.transform = bottomAligned ? 'translateY(-100%)' : 'none';
        const distance = desktop.matches ? viewport.left - bounds.left : viewport.top - bounds.top;
        const override = settings.parallax == null ? NaN : Number(settings.parallax);
        const displacement = distance * (Number.isFinite(override) ? override : strength);
        const offset = reducedMotion.matches ? 0 : Number.isFinite(override)
          ? displacement : Math.max(-80, Math.min(80, displacement));
        setParallaxTranslation(group, offset, desktop.matches, reducedMotion.matches);
      }
    };
    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    desktop.addEventListener('change', update);
    reducedMotion.addEventListener('change', update);
    const observer = new ResizeObserver(update);
    observer.observe(container);
    for (const { page } of entries) observer.observe(page);
    update();
  },
};

export default imageCaptionModule;